import { useRef, useState, useMemo, useEffect } from 'react';
import useFetchJobChangeProcessesInfinite from '../../job_change_processes/api/useFetchJobChangeProcessesInfinite';
import JobChangeProcessListItem from '../../job_change_processes/components/list/JobChangeProcessListItem';
import JobChangeProcessStatusUpdateModal from '../../job_change_processes/components/modal/JobChangeProcessStatusUpdateModal';
import ListItemLoading from '../../job_change_processes/components/list/ListItemLoading';
import ListItemNoData from '../../job_change_processes/components/list/ListItemNoData';
import Loading from '@/components/Loading';
import useOnClickOutside from '../../job_change_processes/hooks/useOnClickOutside';
import useRollbackHandler from '../../job_change_processes/hooks/useRollbackHandler';
import JobChangeProcessSidebar from '../../job_change_processes/components/sidebar/JobChangeProcessDetailSidebar';
import JobChangeProcessLogStatusUpdateModal from '../../job_change_processes/components/modal/JobChangeProcessLogStatusUpdateModal';
import clsx from 'clsx';

type Props = {
  candidateId: string;
};

export default function CandidatesMatchingCardDrawer({ candidateId }: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const searchParams = useMemo(
    () => ({
      candidate_profile_id: candidateId,
      result: ['processing', 'rejected', 'declined', 'offered', 'closed'],
    }),
    [candidateId]
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } =
    useFetchJobChangeProcessesInfinite(searchParams);

  const jobChangeProcesses = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.records);
  }, [data?.pages]);

  const [statusIsShow, setStatusIsShow] = useState(false);
  const [logStatusIsShow, setLogStatusIsShow] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(sidebarRef, () => {
    setIsSidebarOpen(false);
    setIsVisible(false);
  });

  const openSidebar = (isValue: boolean) => {
    setIsSidebarOpen(isValue);
    setIsVisible(isValue);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setTimeout(() => setIsVisible(false), 50);
  };

  // Drawer内のスクロールコンテナを監視して無限スクロールする
  useEffect(() => {
    // Drawer内のスクロールコンテナを直接検索
    const container = document.querySelector<HTMLElement>('[data-candidates-matching-section]');
    if (!container) {
      return;
    }

    // 内側のsectionを探す（実際にスクロールしている要素）
    const section = container.querySelector<HTMLElement>('section');
    if (!section) {
      return;
    }

    // 実際にスクロールしている要素を決定（sectionがスクロール可能な場合はsection、そうでなければcontainer）
    const scrollableElement = section.scrollHeight > section.clientHeight ? section : container;

    let timeoutId: NodeJS.Timeout;
    const debouncedScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = scrollableElement;
        const distanceToBottom = scrollHeight - (scrollTop + clientHeight);

        // 底から200px以内でかつ次のページがある場合にフェッチ
        if (distanceToBottom < 200 && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }, 100);
    };

    scrollableElement.addEventListener('scroll', debouncedScroll);
    return () => {
      scrollableElement.removeEventListener('scroll', debouncedScroll);
      clearTimeout(timeoutId);
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const scrollToTarget = () => {
    // Drawer内ではスクロールコンテナをスクロール
    const container = document.querySelector<HTMLElement>('[data-candidates-matching-section]');
    if (container) {
      container.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const updateJobchangeProcesses = async () => {
    await refetch();
    scrollToTarget();
  };

  const { handleRollback, isRollbackPending } = useRollbackHandler(async () => {
    closeSidebar();
    await updateJobchangeProcesses();
  });

  return (
    <>
      <div className="relative flex h-full flex-col">
        <div className="p-8">
          <div className="relative flex flex-col">
            <div className="sticky top-4 z-[5] flex h-[36px] items-center justify-around rounded-s bg-tag-gray px-6">
              <p className="text-sm font-bold">求職者</p>
              <p className="text-sm font-bold">企業名 / 求人</p>
              <p className="text-sm font-bold">ステータス</p>
              <p className="text-sm font-bold">選考日程</p>
              <p className="text-sm font-bold">メモ</p>
            </div>
            <div className="hidden-scrollbar flex flex-col gap-3 px-1 pt-4">
              {isLoading ? (
                <>
                  {Array.from({ length: 10 }).map((_, key) => {
                    return <ListItemLoading key={key} />;
                  })}
                </>
              ) : (
                <>
                  {jobChangeProcesses.length === 0 ? (
                    <div className="mt-[100px]">
                      <ListItemNoData />
                    </div>
                  ) : (
                    jobChangeProcesses.map((j, key) => {
                      return (
                        <JobChangeProcessListItem
                          key={`${j.id}-${key}`}
                          jobChangeProcess={j}
                          openSidebar={openSidebar}
                          statusModalOpen={() => setStatusIsShow(true)}
                          jobchangeProcessesRefetch={updateJobchangeProcesses}
                          isSidebarOpen={isSidebarOpen}
                        />
                      );
                    })
                  )}
                </>
              )}
            </div>
            {(isFetchingNextPage || (hasNextPage && isLoading)) && (
              <div className="flex justify-center py-md">
                <Loading size="small" />
              </div>
            )}
            <JobChangeProcessStatusUpdateModal
              open={statusIsShow}
              onOpenChange={setStatusIsShow}
              jobchangeProcessesRefetch={updateJobchangeProcesses}
            />
          </div>
        </div>
      </div>
      <div>
        {isVisible && (
          <div
            ref={sidebarRef}
            className={clsx(
              'fixed top-0 right-0 z-50 h-full w-[415px] overflow-hidden bg-white shadow-[0px_0px_2px_0px_rgba(0,_0,_0,_0.12)]',
              isSidebarOpen ? 'animate-slideInRight' : 'animate-fadeOutRight'
            )}
          >
            <div className="flex h-full flex-col overflow-hidden">
              <JobChangeProcessSidebar
                closeSidebar={closeSidebar}
                statusOpen={() => setLogStatusIsShow(true)}
                isSidebarOpen={isSidebarOpen}
                onRollback={handleRollback}
                isRollbackPending={isRollbackPending}
              />
            </div>
          </div>
        )}
        <JobChangeProcessLogStatusUpdateModal
          open={logStatusIsShow}
          onOpenChange={setLogStatusIsShow}
          jobchangeProcessesRefetch={updateJobchangeProcesses}
        />
      </div>
    </>
  );
}
